import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  X, Loader2, FolderOpen, MessageSquare, ThumbsUp, Sparkles, ChevronRight,
  Plus, Search, Check,
} from 'lucide-react'
import {
  getThemeDetail,
  listFeedback,
  addThemeItems,
  type FeedbackThemeDetail,
  type FeedbackItem,
} from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  backlog: { bg: 'bg-secondary', text: 'text-muted-foreground', label: 'Backlog' },
  in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'In Progress' },
  done: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Done' },
  dismissed: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Dismissed' },
}

const MIN_WIDTH = 360
const MAX_WIDTH = 720
const DEFAULT_WIDTH = 480

interface ThemeDrawerProps {
  themeId: number | null
  onClose: () => void
  onItemSelect: (id: number) => void
  onWidthChange?: (width: number) => void
}

export function ThemeDrawer({
  themeId,
  onClose,
  onItemSelect,
  onWidthChange,
}: ThemeDrawerProps) {
  const [detail, setDetail] = useState<FeedbackThemeDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aiSummary, setAiSummary] = useState<string | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [showLinkPicker, setShowLinkPicker] = useState(false)
  const [linkSearch, setLinkSearch] = useState('')
  const [ungroupedItems, setUngroupedItems] = useState<FeedbackItem[]>([])
  const [loadingUngrouped, setLoadingUngrouped] = useState(false)
  const [selectedToLink, setSelectedToLink] = useState<Set<number>>(new Set())
  const [linking, setLinking] = useState(false)
  const isResizing = useRef(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const isOpen = themeId !== null

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    onWidthChange?.(isOpen && !isMobile ? width : 0)
    return () => { onWidthChange?.(0) }
  }, [isOpen, width, isMobile]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!themeId) {
      setDetail(null)
      setError(null)
      setAiSummary(null)
      return
    }
    setLoading(true)
    setError(null)
    setAiSummary(null)
    getThemeDetail(themeId)
      .then((d) => setDetail(d))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load theme'))
      .finally(() => setLoading(false))
  }, [themeId])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const startX = e.clientX
    const startWidth = width
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const delta = startX - e.clientX
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta)))
    }
    const handleMouseUp = () => {
      isResizing.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [width])

  const handleGenerateAiSummary = useCallback(async () => {
    if (!detail || detail.items.length === 0) return
    setAiLoading(true)
    try {
      const res = await fetch(`/api/feedback/ai/theme-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme_id: themeId }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `Request failed: ${res.status}`)
      }
      const data = await res.json()
      setAiSummary(data.summary || 'No summary generated.')
    } catch (err) {
      setAiSummary(`Failed to generate summary: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setAiLoading(false)
    }
  }, [detail, themeId])

  const reloadDetail = useCallback(() => {
    if (!themeId) return
    getThemeDetail(themeId)
      .then((d) => setDetail(d))
      .catch(() => {})
  }, [themeId])

  const handleOpenLinkPicker = useCallback(async () => {
    setShowLinkPicker(true)
    setLinkSearch('')
    setSelectedToLink(new Set())
    setLoadingUngrouped(true)
    try {
      const all = await listFeedback({ per_page: 200 })
      const linkedIds = new Set((detail?.items || []).map((it) => it.id))
      setUngroupedItems(all.items.filter((it) => !linkedIds.has(it.id)))
    } catch {
      setUngroupedItems([])
    } finally {
      setLoadingUngrouped(false)
      setTimeout(() => searchInputRef.current?.focus(), 50)
    }
  }, [detail])

  const toggleLinkItem = useCallback((id: number) => {
    setSelectedToLink((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleLinkSelected = useCallback(async () => {
    if (!themeId || selectedToLink.size === 0) return
    setLinking(true)
    try {
      await addThemeItems(themeId, Array.from(selectedToLink))
      setShowLinkPicker(false)
      setSelectedToLink(new Set())
      reloadDetail()
    } catch {
      // stay open on error
    } finally {
      setLinking(false)
    }
  }, [themeId, selectedToLink, reloadDetail])

  if (!isOpen) return null

  const items = detail?.items || []
  const totalVotes = items.reduce((sum, it) => sum + it.vote_count, 0)

  // Type distribution
  const typeCounts: Record<string, number> = {}
  items.forEach((it) => {
    const t = it.type || 'untyped'
    typeCounts[t] = (typeCounts[t] || 0) + 1
  })

  return createPortal(
    <div
      ref={drawerRef}
      style={{ width: isMobile ? undefined : `${width}px` }}
      className="fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 z-40 bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className="hidden sm:block absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors z-50 group"
      >
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0 bg-card">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-semibold truncate">{detail?.name || 'Theme'}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-secondary transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-600 dark:text-red-400">{error}</div>
        ) : detail ? (
          <div className="p-4 space-y-5">
            {/* Stats row */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <MessageSquare className="w-4 h-4" />
                <span>{items.length} item{items.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <ThumbsUp className="w-4 h-4" />
                <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Description */}
            {detail.description && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</h3>
                <p className="text-sm text-foreground leading-relaxed">{detail.description}</p>
              </div>
            )}

            {/* Type distribution */}
            {Object.keys(typeCounts).length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Type Breakdown</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(typeCounts).map(([type, count]) => {
                    const badge = TYPE_BADGE[type]
                    return (
                      <span key={type} className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${badge?.bg || 'bg-secondary'} ${badge?.text || 'text-muted-foreground'}`}>
                        {badge?.label || type} ({count})
                      </span>
                    )
                  })}
                </div>
              </div>
            )}

            {/* AI Summary */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Summary</h3>
                {!aiSummary && !aiLoading && (
                  <button
                    onClick={handleGenerateAiSummary}
                    disabled={items.length === 0}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-border rounded-md hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3" />
                    Generate
                  </button>
                )}
              </div>
              {aiLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing theme...
                </div>
              ) : aiSummary ? (
                <div className="text-sm text-foreground leading-relaxed bg-secondary/30 rounded-lg p-3 prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                  <Markdown remarkPlugins={[remarkGfm]}>{aiSummary}</Markdown>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">Click Generate to create an AI summary of this theme.</p>
              )}
            </div>

            {/* Items list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Linked Feedback ({items.length})
                </h3>
                {!showLinkPicker && (
                  <button
                    onClick={handleOpenLinkPicker}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-border rounded-md hover:bg-secondary transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Link
                  </button>
                )}
              </div>

              {/* Link picker */}
              {showLinkPicker && (
                <div className="border border-primary/30 rounded-lg mb-3 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/20">
                    <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search feedback to link..."
                      value={linkSearch}
                      onChange={(e) => setLinkSearch(e.target.value)}
                      className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                    />
                    <button
                      onClick={() => setShowLinkPicker(false)}
                      className="p-0.5 rounded hover:bg-secondary"
                    >
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {loadingUngrouped ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      ungroupedItems
                        .filter((it) =>
                          !linkSearch || it.title.toLowerCase().includes(linkSearch.toLowerCase())
                        )
                        .slice(0, 30)
                        .map((it) => {
                          const selected = selectedToLink.has(it.id)
                          return (
                            <button
                              key={it.id}
                              onClick={() => toggleLinkItem(it.id)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/30 transition-colors ${
                                selected ? 'bg-primary/5' : ''
                              }`}
                            >
                              <div className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center ${
                                selected ? 'bg-primary border-primary' : 'border-border'
                              }`}>
                                {selected && <Check className="w-3 h-3 text-primary-foreground" />}
                              </div>
                              <span className="flex-1 truncate">{it.title}</span>
                              {it.type && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${TYPE_BADGE[it.type]?.bg || 'bg-secondary'} ${TYPE_BADGE[it.type]?.text || 'text-muted-foreground'}`}>
                                  {TYPE_BADGE[it.type]?.label || it.type}
                                </span>
                              )}
                            </button>
                          )
                        })
                    )}
                    {!loadingUngrouped && ungroupedItems.filter((it) =>
                      !linkSearch || it.title.toLowerCase().includes(linkSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                        No matching feedback found
                      </div>
                    )}
                  </div>
                  {selectedToLink.size > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-secondary/10">
                      <span className="text-xs text-muted-foreground">{selectedToLink.size} selected</span>
                      <button
                        onClick={handleLinkSelected}
                        disabled={linking}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity"
                      >
                        {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Link {selectedToLink.size}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                {items.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                    No feedback linked yet
                  </div>
                ) : (
                  items.map((item) => (
                    <ThemeItemRow
                      key={item.id}
                      item={item}
                      onClick={() => { onClose(); onItemSelect(item.id) }}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

function ThemeItemRow({ item, onClick }: { item: FeedbackItem; onClick: () => void }) {
  const typeBadge = item.type ? TYPE_BADGE[item.type] : null
  const statusBadge = STATUS_BADGE[item.status] || STATUS_BADGE.backlog

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-secondary/30 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{item.title}</span>
        <span className="text-xs text-muted-foreground">{formatRelativeTime(item.created_at)}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs text-muted-foreground">{item.vote_count}</span>
        <ThumbsUp className="w-3 h-3 text-muted-foreground" />
      </div>
      {typeBadge && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${typeBadge.bg} ${typeBadge.text}`}>
          {typeBadge.label}
        </span>
      )}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${statusBadge.bg} ${statusBadge.text}`}>
        {statusBadge.label}
      </span>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  )
}
